import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { PencilLine, Radio, Search, User } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../store/auth';
import { searchStreams } from '../../api/streams';
import { getSubscribers } from '../../api/subscribers';
import type { NavSection } from '../../lib/roles';

interface Result {
  key: string;
  label: string;
  hint: string;
  to: string;
  group: 'Pages' | 'Channels' | 'Subscribers';
  Icon: React.ComponentType<{ size?: number; className?: string }>;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

// CommandPalette jumps to a page, channel or subscriber by name. Ctrl+K
// (Cmd+K on macOS) opens it from anywhere.
export function CommandPalette({
  open,
  onOpenChange,
  sections,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sections: NavSection[];
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-gray-900/40 dark:bg-black/60" />
        {/* Content unmounts when closed, so every open starts with an empty search. */}
        <PaletteBody sections={sections} close={() => onOpenChange(false)} />
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PaletteBody({ sections, close }: { sections: NavSection[]; close: () => void }) {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const canSeeChannels = role === 'admin' || role === 'client';
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const q = query.trim().toLowerCase();
  const remoteQ = useDebounced(q, 200);

  const channels = useQuery({
    queryKey: ['palette-streams', remoteQ],
    queryFn: () => searchStreams(remoteQ),
    enabled: canSeeChannels && remoteQ.length >= 2,
    staleTime: 30_000,
  });
  const subscribers = useQuery({
    queryKey: ['palette-subscribers', remoteQ],
    queryFn: () => getSubscribers({ search: remoteQ, limit: 5 }),
    enabled: remoteQ.length >= 2,
    staleTime: 30_000,
  });

  const results = useMemo<Result[]>(() => {
    const pages: Result[] = sections.flatMap((s) =>
      s.items
        .filter((i) => !q || i.label.toLowerCase().includes(q) || s.title.toLowerCase().includes(q))
        .map((i) => ({ key: 'p' + i.to, label: i.label, hint: s.title, to: i.to, group: 'Pages' as const, Icon: i.Icon }))
    );
    // A tool reached from the Streams page, not from the sidebar.
    if (canSeeChannels && q && 'rename channels'.includes(q)) {
      pages.unshift({ key: 'p/streams/rename', label: 'Rename channels', hint: 'Live', to: '/streams/rename', group: 'Pages', Icon: PencilLine });
    }
    const out = q ? pages.slice(0, 8) : pages.slice(0, 6);
    if (q.length >= 2 && remoteQ === q) {
      for (const st of (channels.data ?? []).slice(0, 5)) {
        out.push({ key: 's' + st.id, label: st.name, hint: st.stream_status === 'live' ? 'Live' : 'Stopped', to: `/streams/${st.id}/timeline`, group: 'Channels', Icon: Radio });
      }
      for (const sub of (subscribers.data?.items ?? []).slice(0, 5)) {
        out.push({ key: 'u' + sub.id, label: sub.username, hint: 'Subscriber', to: `/subscribers/${sub.id}/edit`, group: 'Subscribers', Icon: User });
      }
    }
    return out;
  }, [sections, q, remoteQ, channels.data, subscribers.data, canSeeChannels]);

  const active = Math.min(selected, Math.max(results.length - 1, 0));
  const go = (r: Result | undefined) => {
    if (!r) return;
    close();
    navigate(r.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((active + 1) % Math.max(results.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((active - 1 + results.length) % Math.max(results.length, 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active]);
    }
  };

  const loading = q.length >= 2 && (remoteQ !== q || channels.isFetching || subscribers.isFetching);

  return (
    <Dialog.Content
      aria-describedby={undefined}
      className="fixed left-1/2 top-[12vh] z-50 w-[calc(100%-32px)] max-w-xl -translate-x-1/2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
      onKeyDown={onKeyDown}
    >
      <Dialog.Title className="sr-only">Quick search</Dialog.Title>
      <label className="flex h-14 items-center gap-3 border-b border-gray-200 px-4 dark:border-slate-700">
        <Search size={17} className="text-gray-400 dark:text-slate-500" />
        <span className="sr-only">Search pages, channels and subscribers</span>
        <input
          autoFocus
          // Inline: the app-wide :focus-visible ring is unlayered CSS, which
          // beats Tailwind's layered utilities; the open dialog is focus cue enough.
          style={{ outline: 'none' }}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(0); }}
          placeholder={canSeeChannels ? 'Go to a page, channel or subscriber…' : 'Go to a page or subscriber…'}
          className="min-w-0 flex-1 bg-transparent text-base text-gray-900 placeholder:text-gray-400 dark:text-slate-100 dark:placeholder:text-slate-500"
        />
        <kbd className="rounded border border-gray-300 px-1.5 py-px text-[10px] font-semibold text-gray-500 dark:border-slate-600 dark:text-slate-400">Esc</kbd>
      </label>
      <div className="max-h-[60vh] overflow-y-auto p-2" role="listbox" aria-label="Results">
        {results.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-gray-500 dark:text-slate-400">
            {loading ? 'Searching…' : `Nothing matches “${query}”.`}
          </p>
        ) : (
          results.map((r, i) => (
            <div key={r.key}>
              {(i === 0 || results[i - 1].group !== r.group) && (
                <p className="px-3 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-500">{r.group}</p>
              )}
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                onMouseMove={() => setSelected(i)}
                onClick={() => go(r)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm',
                  i === active ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300' : 'text-gray-700 dark:text-slate-300'
                )}
              >
                <r.Icon size={15} className={i === active ? '' : 'text-gray-400 dark:text-slate-500'} />
                <span className="flex-1 truncate font-medium">{r.label}</span>
                <span className="text-xs text-gray-400 dark:text-slate-500">{r.hint}</span>
              </button>
            </div>
          ))
        )}
        {loading && results.length > 0 && (
          <p className="px-3 py-2 text-xs text-gray-400 dark:text-slate-500">Searching channels and subscribers…</p>
        )}
      </div>
      <div className="flex flex-wrap gap-4 border-t border-gray-200 px-4 py-2.5 text-xs text-gray-500 dark:border-slate-700 dark:text-slate-400">
        <span>↑ ↓ to move</span>
        <span>Enter to open</span>
        <span>Ctrl K to open from anywhere</span>
      </div>
    </Dialog.Content>
  );
}
